/**
 * @class An extension of the THREE orbit camera.
 * Here we implement movement for the orbit camera used in the viewer.
 */

// @ts-ignore
import TWEEN from '@tweenjs/tween.js';
import * as THREE from 'three';
import { Box3, Euler, Object3D, Quaternion, Vector2, Vector3 } from 'three';

import { CameraTransformation } from '../../../../typings/api/skymap/rest/v0/.common';
import { AssetManager } from '../asset-manager';
import { CoordinateManager } from '../coordinate-manager';
import { Dxf } from '../elements/dxf';
import { Glb } from '../elements/glb';
import { TerrainModel } from '../elements/terrain-model';
import { Interpolation, InterpolationType } from '../utilities/interpolation';
import { clamp } from '../utilities/math-helpers';
import { Pick } from '../utilities/pick';
import type { SkyviewFpsCamera } from './skyview-fps-camera';
import type { SkyviewOrthographicCamera } from './skyview-orthographic-camera';
import { CameraType, PerspectiveCameraParameters, SkyviewCamera } from './types';

export function getCameraScreenPosition(
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  point: Vector3,
  width: number,
  height: number,
) {
  const vector = new THREE.Vector3();

  vector.copy(point);

  // map to normalized device coordinate (NDC) space.
  vector.project(camera);

  // map to 2D screen space.
  const x = Math.round(((vector.x + 1) * width) / 2);
  const y = Math.round(((-vector.y + 1) * height) / 2);

  return new THREE.Vector2(x, y);
}

export class SkyviewPerspectiveCamera extends THREE.PerspectiveCamera implements SkyviewCamera {
  cameraType: CameraType;

  private minDistance: number;
  private maxDistance: number;

  private zoomSpeed: number;

  private focusPoint: THREE.Object3D;
  private focusPointSphere: THREE.Mesh;

  width = 0;
  height = 0;

  private material: THREE.MeshBasicMaterial;

  private lastTimeMoved = Date.now();

  constructor(fov: number, aspect: number, near: number, far: number) {
    super(fov, aspect, near, far);

    this.cameraType = CameraType.PerspectiveCamera;

    // Manage large zoom that results in floating point error when generating ray
    // Limit the zoom to 10,000,000,000 m (1,000,000 metric miles)
    // This will generate small precision errors when picking further away, but it is dismissible
    // (the distance to the moon is about 38,500 metric miles, so nothing would be visible, barely even the earth)
    this.minDistance = 0.1;
    this.maxDistance = Math.pow(10, 10);

    this.zoomSpeed = 0.0001;
    this.focusPoint = new THREE.Object3D();

    this.material = new THREE.MeshBasicMaterial({
      color: new THREE.Color('#57f'),
      transparent: true,
      opacity: 1,
      depthTest: false,
      visible: false,
    });

    this.focusPointSphere = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), this.material);
    this.focusPoint.add(this.focusPointSphere);
    this.focusPoint.add(this);

    this.position.setZ(this.minDistance);
  }

  timeSinceTransformed(): number {
    return this.lastTimeMoved;
  }

  /**
   * The focus point of the camera
   */
  getFocusPoint(): THREE.Object3D {
    return this.focusPoint;
  }

  /**
   *  Update the position of the camera focus point
   * @param position
   */
  setFocusPointPosition(position: THREE.Vector3) {
    return this.focusPoint.position.copy(position);
  }

  /**
   *  Translates the camera according to the vector
   * @param vector
   */
  move(vector: THREE.Vector3) {
    this.updateLastMoved(vector.length() > 0);

    this.focusPoint.position.add(vector);
  }

  /**
   *  Set if the focus points should be displayed
   * @param visible
   */
  setDisplayFocusPoint(visible: boolean) {
    this.material.visible = visible;
  }

  /**
   *  Calculate and rotate around a target object
   * @param phiDelta
   * @param thetaDelta
   * @param target
   */
  rotate(phiDelta: number, thetaDelta: number, target?: THREE.Vector3) {
    this.updateLastMoved(phiDelta !== 0 || thetaDelta !== 0);
    const worldQuat = new THREE.Quaternion();
    this.focusPoint.getWorldQuaternion(worldQuat);

    this.focusPoint.rotateOnWorldAxis(new THREE.Vector3(0, 0, 1), phiDelta);

    // Ensure we do not 'flip' the camera when rotating up and down
    const worldRot = new THREE.Euler();
    worldRot.setFromQuaternion(worldQuat);
    worldRot.reorder('ZYX');
    if (worldRot.x + thetaDelta <= 0) {
      thetaDelta = -worldRot.x;
    } else if (worldRot.x + thetaDelta >= Math.PI - 0.01) {
      thetaDelta = Math.PI - 0.01 - worldRot.x;
    }
    this.focusPoint.rotateOnAxis(new THREE.Vector3(1, 0, 0), thetaDelta);
  }

  /**
   * Rotate around the actual camera position
   * @param phiDelta - Rotation around the Z-axis (azimuthal rotation)
   * @param thetaDelta - Rotation around the local X-axis (elevation rotation)
   */
  rotatePivot(phi: number, theta: number) {
    this.updateLastMoved(phi !== 0 || theta !== 0);

    const worldPosition = new Vector3();
    this.getWorldPosition(worldPosition); // Get the child's world position

    // Create rotation quaternions in local space
    const rotationAroundZ = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -phi); // Rotate around Z-axis (azimuth)
    const rotationAroundLocalX = new Quaternion().setFromAxisAngle(
      new Vector3(1, 0, 0).applyQuaternion(this.focusPoint.quaternion),
      -theta,
    ); // Rotate around local X-axis (elevation)

    // Combine rotations in the correct order
    const combinedRotation = new Quaternion();
    combinedRotation.multiplyQuaternions(rotationAroundZ, rotationAroundLocalX);

    // Apply the combined rotation to the object's quaternion
    this.focusPoint.quaternion.multiplyQuaternions(combinedRotation, this.focusPoint.quaternion);

    // Adjust the object's position to maintain the child's world position
    const newWorldPosition = new Vector3();
    this.getWorldPosition(newWorldPosition);

    const offset = newWorldPosition.sub(worldPosition); // Calculate the offset
    this.focusPoint.position.sub(offset); // Adjust the object's position to compensate
  }

  /**
   * Turns the camera around its own position, as when looking around, without tipping it over:
   * from looking straight down to straight up.
   * @param phi - Rotation around the Z-axis (azimuthal rotation)
   * @param theta - Rotation around the local X-axis (elevation rotation)
   */
  lookAround(phi: number, theta: number) {
    // The focus point is turned around Z, then tilted around its X from looking straight down
    const tilt = new Euler().setFromQuaternion(
      this.focusPoint.getWorldQuaternion(new Quaternion()),
      'ZYX',
    ).x;
    this.rotatePivot(phi, clamp(theta, tilt - (Math.PI - 0.01), tilt - 0.01));
  }

  /**
   * Sets the zoom based on desired distance to the focus point.
   * Note that this function clamps the distance between a min and max value.
   */
  setZoom(distanceToFocusPoint: number) {
    const clampedZoom = clamp(distanceToFocusPoint, this.minDistance, this.maxDistance);

    this.updateLastMoved(clampedZoom !== this.position.z);
    this.position.setZ(clampedZoom);
    this.focusPointSphere.scale.setScalar(this.position.z / (this.minDistance * 1000));
  }

  /**
   *   Zoom the camera according the length value
   * @param deltaZoom
   */
  zooming(deltaZoom: number): number {
    this.updateLastMoved(deltaZoom !== 0);
    const numberSign = deltaZoom < 0 ? -1 : 1;

    // Fix so that we still have a reasonable zoom speed even if we are really close to a target
    const zoomDist =
      Math.max(Math.abs((deltaZoom * this.zoomSpeed * this.position.z) / this.minDistance), 0.1) *
      numberSign;
    if (this.position.z + zoomDist > this.minDistance) {
      this.setZoom(this.position.z + zoomDist);
      return 0;
    }

    return zoomDist;
  }

  /**
   *   Updates the aspect ratio. This will occur as the window size changes.
   * @param width
   * @param height
   */
  updateAspect(width: number, height: number) {
    this.aspect = width / height;
    this.width = width;
    this.height = height;

    this.updateProjectionMatrix();
    this.updateLastMoved();
  }

  cloneParameters(offset: Vector3): PerspectiveCameraParameters {
    return {
      focusPointPosition: this.focusPoint.position.clone().add(offset),
      focusPointRotation: this.focusPoint.rotation.clone(),
      aspect: this.aspect,
      cameraDistanceToFocusPoint: this.position.z,
    };
  }

  /**
   *  Copy the view from another camera
   * @param camera
   */
  copyViewFromCamera(camera: SkyviewCamera, skipOrigoTransformation = false) {
    const offset = skipOrigoTransformation
      ? new Vector3(0, 0, 0)
      : CoordinateManager.getInstance().origoDifference;

    switch (camera.cameraType) {
      case CameraType.FPSCamera: {
        const fpsCamera = camera as SkyviewFpsCamera;

        this.setParameters({
          aspect: fpsCamera.aspect,
          focusPointPosition: fpsCamera.getWorldPosition(new Vector3()).clone().add(offset),
          focusPointRotation: new Euler(0, 0, 0),
          cameraDistanceToFocusPoint: 25,
        });

        break;
      }
      case CameraType.PerspectiveCamera: {
        const perspectiveCamera = camera as SkyviewPerspectiveCamera;
        this.setParameters(perspectiveCamera.cloneParameters(offset));
        this.width = perspectiveCamera.width;
        this.height = perspectiveCamera.height;
        break;
      }
      case CameraType.OrthographicCamera: {
        const orthographicCamera = camera as unknown as SkyviewOrthographicCamera;

        const focusPointPosition = orthographicCamera.getLookAtPosition().add(offset);
        const distanceToFocusPoint = orthographicCamera.getViewDistanceByFov(this.fov);

        this.setParameters({
          aspect: orthographicCamera.getAspectRatio(),
          focusPointPosition: focusPointPosition,
          focusPointRotation: new Euler(0, 0, 0),
          cameraDistanceToFocusPoint: distanceToFocusPoint,
        });

        const widthAndHeight = orthographicCamera.getWidthAndHeight();
        this.width = widthAndHeight.width;
        this.height = widthAndHeight.height;

        break;
      }
      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Unknown camera type: ${camera.cameraType}`);
    }
  }

  /**
   *  Moves and rotates the perspective camera
   * @param cameraParameter
   */
  setParameters(cameraParameter: PerspectiveCameraParameters) {
    this.focusPoint.add(this.focusPointSphere);
    this.focusPoint.add(this);

    this.focusPoint.position.copy(cameraParameter.focusPointPosition);
    this.focusPoint.rotation.copy(cameraParameter.focusPointRotation);

    this.position.set(0, 0, 0);
    this.setZoom(cameraParameter.cameraDistanceToFocusPoint);
    this.aspect = cameraParameter.aspect;

    this.updateProjectionMatrix();
  }

  resolveIntersectionDistance(orthographicCamera: SkyviewOrthographicCamera) {
    const filter = (asset: Object3D) => {
      return (
        asset.visible &&
        (asset instanceof Dxf || asset instanceof Glb || asset instanceof TerrainModel)
      );
    };

    const pickedPosition = Pick.instance.assetPoint(0, 0, filter, { camera: orthographicCamera });
    const assetsEncapsulatingBoundingBox = new Box3().setFromObject(AssetManager.instance);

    if (pickedPosition) {
      return orthographicCamera.position.distanceTo(pickedPosition);
    } else {
      const assetCenter = assetsEncapsulatingBoundingBox.getCenter(new Vector3());
      return orthographicCamera.position.z - assetCenter.z;
    }
  }

  /**
   *  Calculate the distance from a point to the camera
   * @param point
   */
  getDistanceToPoint(point: THREE.Vector3) {
    return this.getWorldPosition(new THREE.Vector3()).distanceTo(point);
  }

  /**
   * @param box
   * @description Calculate the distance from a bounding box to the camera
   */
  getDistanceToBoundingBox(box: THREE.Box3) {
    return box.distanceToPoint(this.getWorldPosition(new THREE.Vector3()));
  }

  /**
   * @param box
   * @param offset
   * @param onComplete
   * @description Move the camera to focus on the box
   */
  focusOnBox(box: THREE.Box3, offset = 1.1, onComplete?: () => any) {
    const center: THREE.Vector3 = box.getCenter(new THREE.Vector3());
    const size: THREE.Vector3 = box.getSize(new THREE.Vector3());

    // get the max side of the bounding box (fits to width OR height as needed )
    const maxDim = size.length();
    const fov = this.fov * (Math.PI / 180);
    let cameraZ = Math.abs((maxDim / 4) * Math.tan(fov * 2));
    cameraZ *= offset; // zoom out a little so that objects don't fill the screen

    new TWEEN.Tween(this.focusPoint.position)
      .to(new THREE.Vector3(center.x, center.y, center.z), 1000)
      .easing(TWEEN.Easing.Quadratic.Out)
      .start();

    const zoomerTween = { z: this.position.z };
    new TWEEN.Tween(zoomerTween)
      .to({ z: cameraZ }, 1000)
      .onUpdate(() => {
        this.setZoom(zoomerTween.z);
        this.refreshCameraProjection();
      })
      .onComplete(() => {
        if (onComplete) {
          onComplete();
        }
      })
      .easing(TWEEN.Easing.Quadratic.Out)
      .start();
  }

  /**
   * Refreshes the cameras matrix world and projection matrix. This is necessary
   * to do during tween for example to ensure the camera renders objects consistently
   * while it's moving otherwise objects can appear out of sync or as if they lag behind.
   */
  private refreshCameraProjection() {
    this.updateMatrixWorld(true);
    this.updateProjectionMatrix();
  }

  /**
   *  Convert a point to screen position.
   * @param point
   */
  getScreenPosition(point: THREE.Vector3): THREE.Vector2 {
    return getCameraScreenPosition(this, point, this.width, this.height);
  }

  /**
   *  Returns the position that the camera focus on.
   */
  getLookAtPosition(): THREE.Vector3 {
    return this.focusPoint.position;
  }

  /**
   *  Generate the frustum based on the camera.
   * @returns {THREE.Frustum}
   */
  getFrustum() {
    this.updateMatrix(); // make sure camera's local matrix is updated.
    this.updateMatrixWorld(false); // make sure camera's world matrix is updated.

    this.matrixWorldInverse = this.matrixWorld.clone().invert();

    const frustum = new THREE.Frustum();
    frustum.setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(this.projectionMatrix, this.matrixWorldInverse),
    );
    return frustum;
  }

  /**
   *  Create a Raycaster from the camera
   * @param x
   * @param y
   */
  getRay(x: number, y: number): THREE.Raycaster {
    const rayCaster = new THREE.Raycaster();
    rayCaster.setFromCamera(new Vector2(x, y), this);
    rayCaster.params.Points = { threshold: 0.25 };
    return rayCaster;
  }

  /**
   *  Set the position of the camera
   * @param position
   */
  setPosition(position: THREE.Vector2): void {
    this.updateLastMoved();
    this.focusPoint.position.set(position.x, position.y, this.focusPoint.position.z);
  }

  /**
   * This is to be able to render the related camera artefacts
   * @param scene
   */
  addToScene(scene: THREE.Scene): void {
    scene.add(this.focusPoint);
    this.focusPoint.add(this.focusPointSphere);
    this.focusPoint.add(this);
  }

  /**
   * Detach this camera from its parent
   */
  removeFromScene() {
    if (this.focusPoint.parent) {
      this.focusPoint.parent.remove(this.focusPoint);
    }
  }

  /**
   *  Transform the transformation matrix to target
   * @param position
   * @param rotation
   * @param duration
   */
  transformTo(
    position: Vector3 = this.focusPoint.position.clone(),
    rotation: Quaternion = this.focusPoint.quaternion.clone(),
    duration = 0,
  ) {
    if (duration <= 0) {
      this.focusPoint.quaternion.slerp(rotation, 1);
      this.focusPoint.position.lerp(position, 1);
    } else {
      const startQuaternion = this.focusPoint.quaternion.clone();
      const startPosition = this.focusPoint.position.clone();
      new Interpolation(
        {
          duration,
          type: InterpolationType.Ease,
        },
        (interpolationValue) => {
          this.focusPoint.quaternion.copy(
            startQuaternion.clone().slerp(rotation, interpolationValue),
          );
          this.focusPoint.position.copy(startPosition.lerp(position, interpolationValue));
          this.updateLastMoved();
        },
      );
    }
  }

  /**
   *  Returns the THREE camera object
   */
  getTHREECamera(): THREE.PerspectiveCamera {
    return this;
  }

  /**
   *  Extracts the camera transformation for the camera
   */
  extractTransformation(): CameraTransformation {
    const quaternion = this.focusPoint.getWorldQuaternion(new Quaternion());
    return {
      position: this.focusPoint.getWorldPosition(new Vector3()),
      // Just sending quaternion not possible,
      // as the parameters are named _x, _y, _z, _w, which conflicts with the request structure
      rotation: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w },
      zoom: this.position.z,
    };
  }

  /**
   *  Applies the camera transformation for the camera
   * @param transformation
   */
  applyTransformation(transformation: CameraTransformation) {
    const position = new Vector3(
      transformation.position.x,
      transformation.position.y,
      transformation.position.z,
    );
    const quaternion = new Quaternion(
      transformation.rotation.x,
      transformation.rotation.y,
      transformation.rotation.z,
      transformation.rotation.w,
    );
    this.focusPoint.position.copy(position);
    this.focusPoint.setRotationFromQuaternion(quaternion);
    this.setZoom(transformation.zoom);
  }

  private updateLastMoved(moved: boolean = true) {
    this.lastTimeMoved = moved ? Date.now() : this.lastTimeMoved;
  }
}
